import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { getSummonerProfileContext } from "components/SummonerProfiles/SummonerProfileContext";
import { useSummonerProfile } from "api/summonerProfileApi";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { INTERNAL_CURRENT_SEASON, seasonMap } from "@ugg/shared/constants/seasons";
import { getShortName } from "lib/region_helper";
import { convertRomanNumeral, capitalizeFirstLetter } from "lib/general-helper";

function getRankText(rankScore) {
  if (!rankScore || !rankScore.tier) {
    return "";
  }

  switch (rankScore.tier.toLowerCase()) {
    case "master":
    case "grandmaster":
    case "challenger":
      return capitalizeFirstLetter(rankScore.tier);
    default:
      return `${capitalizeFirstLetter(rankScore.tier)} ${convertRomanNumeral(rankScore.rank)}`;
  }
}

function getRankScore(rankScores, queueType) {
  return (
    rankScores &&
    rankScores.find(
      (rankScore) => parseInt(rankScore.seasonId) === parseInt(INTERNAL_CURRENT_SEASON) && rankScore.queueType === queueType,
    )
  );
}

const DefaultSummonerProfileSEO = (props) => {
  const { getProfileIconImg } = getRiotAssetsContext();
  const context = getSummonerProfileContext();
  const { summonerName, region, previewMode } = context;
  const location = useLocation();

  const { data, loading, error } = useSummonerProfile(region, summonerName, {}, "");

  if (previewMode || !data || loading || error) {
    return null;
  }

  const { profileInit, fetchProfileRanks } = data || {};
  const { playerInfo, memberStatus, lastModified } = profileInit || {};
  const { rankScores } = fetchProfileRanks || {};
  const {
    summonerName: realSummonerName, // correct spelling
    regionId,
    iconId,
  } = playerInfo || {};

  const realSeason = `${seasonMap[INTERNAL_CURRENT_SEASON]?.season}-${seasonMap[INTERNAL_CURRENT_SEASON]?.split}`;

  const soloRank = getRankText(getRankScore(rankScores, "ranked_solo_5x5"));
  const flexRank = getRankText(getRankScore(rankScores, "ranked_flex_sr"));

  const title = `${realSummonerName} S${realSeason} LoL Profile (${getShortName(regionId)}) | ${
    soloRank ? `${soloRank} Ranked Solo, ` : ""
  }${flexRank ? `${flexRank} Ranked Flex, ` : ""}Champion Stats + Match History for Normals, ARAM, All Modes`;
  const description = `Complete match history and champion stats for ${realSummonerName}'s League of Legends Profile (${getShortName(
    regionId,
  )}). See ranks, LP per game, and recent performances in Ranked, Normals, ARAM, and all modes.`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta property="description" content={description} />
      <meta property="og:site_name" content="U.GG" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={getProfileIconImg(iconId)} />
      <meta property="og:url" content={`https://u.gg${location.pathname}${location.search}`} />
    </Helmet>
  );
};

export default DefaultSummonerProfileSEO;
